'use client';

import { Select } from '@/design-system';
import { useLocale, locales } from './LocaleProvider';
import { localeLabels, localeCountryCodes } from '@/i18n/config';
import { getFlagEmoji } from '@/lib/utils';

interface LanguageSwitcherProps {
  className?: string;
  /** Compact 34px-tall trigger for dense toolbars (e.g. the onboarding shell),
   * height-matched to ThemeSwitcher. Default renders the full-size dropdown used
   * in the account menu. */
  compact?: boolean;
}

// Dropdown locale picker. Uses the design-system Select (rendered through a
// portal so it escapes the account-menu popover's overflow) and is driven off
// the shared `locales` list, so adding a new language to `@/i18n/config` makes
// it appear here automatically — no per-language UI changes needed.
export default function LanguageSwitcher({ className = '', compact = false }: LanguageSwitcherProps) {
  const { locale, setLocale } = useLocale();

  const options = locales.map((loc) => ({
    label: localeLabels[loc],
    value: loc,
    icon: (
      <span className="text-base leading-none flex-shrink-0">
        {getFlagEmoji(localeCountryCodes[loc])}
      </span>
    ),
  }));

  return (
    <Select
      value={locale}
      onChange={(v) => setLocale(v as (typeof locales)[number])}
      options={options}
      portal
      size={compact ? 'sm' : 'md'}
      className={className}
    />
  );
}
